import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Loader2, FilePlus2, AlertCircle, CheckCircle2, X } from 'lucide-react';
import api from '../api/client';
import { toast } from '../hooks/use-toast';

const EDITABLE_FIELDS = [
  { key: 'startDate', label: 'Date de début', type: 'date' },
  { key: 'endDate', label: 'Date de fin', type: 'date' },
  { key: 'location', label: 'Lieu', type: 'text' },
  { key: 'mode', label: 'Mode', type: 'select', options: ['Présentiel', 'Distanciel', 'Hybride'] },
  { key: 'duration', label: 'Durée (heures)', type: 'number' },
  { key: 'capacity', label: 'Capacité', type: 'number' },
  { key: 'trainer', label: 'Formateur', type: 'text' },
];

function fieldDisplay(value) {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

/**
 * Dialog to create an amendment when a session is locked.
 * Shows current contractual fields, lets the admin propose new values,
 * captures the reason, then POSTs to /api/sessions/{id}/amendments which
 * also emails the client signer.
 */
export default function AmendmentDialog({ session, open, onOpenChange, onCreated }) {
  const [reason, setReason] = useState('');
  const [pendings, setPendings] = useState({}); // {field: newValue}
  const [submitting, setSubmitting] = useState(false);
  const [list, setList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  useEffect(() => {
    if (!open || !session) return;
    setReason('');
    setPendings({});
    setLoadingList(true);
    api.get(`/sessions/${session.id}/amendments`)
      .then(({ data }) => setList(data || []))
      .catch(() => setList([]))
      .finally(() => setLoadingList(false));
  }, [open, session]);

  if (!session) return null;

  const proposedChanges = EDITABLE_FIELDS
    .filter((f) => pendings[f.key] !== undefined && String(pendings[f.key]) !== String(session[f.key] ?? ''))
    .map((f) => ({
      field: f.key,
      label: f.label,
      before: fieldDisplay(session[f.key]),
      after: fieldDisplay(pendings[f.key]),
    }));

  const onSubmit = async () => {
    if (!reason.trim()) {
      toast({ title: 'Motif requis', description: 'Indiquez la raison de l\'avenant.', variant: 'destructive' });
      return;
    }
    if (proposedChanges.length === 0) {
      toast({ title: 'Aucune modification', description: 'Modifiez au moins un champ.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/sessions/${session.id}/amendments`, {
        reason,
        changes: proposedChanges,
      });
      toast({
        title: 'Avenant créé ✓',
        description: data.email_sent
          ? `Lien de signature envoyé à ${data.client_email}.`
          : `À envoyer manuellement (${data.client_email})`,
      });
      setList((prev) => [data, ...prev]);
      setPendings({});
      setReason('');
      if (onCreated) onCreated(data);
    } catch (e) {
      toast({
        title: 'Erreur',
        description: e?.response?.data?.detail || 'Création impossible',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const onCancelAmendment = async (aid) => {
    try {
      await api.post(`/amendments/${aid}/cancel`);
      setList((prev) => prev.map((a) => (a.id === aid ? { ...a, status: 'cancelled' } : a)));
      toast({ title: 'Avenant annulé' });
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Annulation impossible', variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[760px] max-h-[90vh] overflow-y-auto rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <FilePlus2 className="w-5 h-5 text-[#4F46E5]" /> Créer un avenant à la convention
          </DialogTitle>
          <DialogDescription>
            Cette session est verrouillée. Pour modifier un élément contractuel, vous devez établir un avenant
            qui sera envoyé au client pour signature électronique.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="rounded-xl bg-[#FEF3E7] border border-[#FCD9A6] p-3 flex items-start gap-2.5">
            <AlertCircle className="w-4 h-4 text-[#B45309] mt-0.5 shrink-0" />
            <div className="text-[12.5px] text-[#92400E] leading-relaxed">
              Modifiez les champs ci-dessous. Seuls les champs réellement modifiés seront inclus dans l'avenant.
              Le client recevra un email avec un lien pour consulter et signer l'avenant.
            </div>
          </div>

          {/* Editable fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {EDITABLE_FIELDS.map((f) => {
              const cur = session[f.key];
              const pending = pendings[f.key];
              const dirty = pending !== undefined && String(pending) !== String(cur ?? '');
              return (
                <div key={f.key} className="rounded-xl border border-[#EFF0F6] p-3">
                  <label className="text-[11.5px] uppercase tracking-wide text-[#6B6C85] font-semibold">{f.label}</label>
                  <div className="text-[11px] text-[#8E8FA5] mt-0.5 mb-2">Actuel : <span className="text-[#1A1B2E] font-semibold">{fieldDisplay(cur)}</span></div>
                  {f.type === 'select' ? (
                    <select
                      data-testid={`amendment-${f.key}`}
                      className={`w-full h-9 rounded-lg border px-2 text-[13px] bg-white ${dirty ? 'border-[#4F46E5] bg-[#EEF2FF]' : 'border-[#EFF0F6]'}`}
                      value={pending ?? cur ?? ''}
                      onChange={(e) => setPendings({ ...pendings, [f.key]: e.target.value })}
                    >
                      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input
                      data-testid={`amendment-${f.key}`}
                      type={f.type}
                      className={`w-full h-9 rounded-lg border px-2 text-[13px] bg-white ${dirty ? 'border-[#4F46E5] bg-[#EEF2FF]' : 'border-[#EFF0F6]'}`}
                      value={pending ?? cur ?? ''}
                      onChange={(e) => setPendings({ ...pendings, [f.key]: e.target.value })}
                    />
                  )}
                  {dirty && (
                    <div className="text-[10.5px] text-[#4F46E5] mt-1.5 font-semibold flex items-center gap-1">
                      → Nouvelle valeur : {fieldDisplay(pending)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Reason */}
          <div className="rounded-xl border border-[#EFF0F6] p-3">
            <label className="text-[11.5px] uppercase tracking-wide text-[#6B6C85] font-semibold">Motif de l'avenant *</label>
            <textarea
              data-testid="amendment-reason"
              rows={3}
              className="w-full mt-1 rounded-lg border border-[#EFF0F6] px-3 py-2 text-[13px] focus:border-[#4F46E5] outline-none"
              placeholder="Ex. Report d'une journée pour cause de force majeure ; changement de lieu suite à indisponibilité de la salle initiale…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          {proposedChanges.length > 0 && (
            <div className="rounded-xl bg-[#EEF2FF] border border-[#C7D2FE] p-3">
              <div className="text-[11.5px] uppercase tracking-wide text-[#4F46E5] font-semibold mb-1.5">
                {proposedChanges.length} modification{proposedChanges.length > 1 ? 's' : ''} sera{proposedChanges.length > 1 ? 'ont' : ''} inclus
              </div>
              <ul className="text-[12.5px] text-[#1A1B2E] space-y-0.5">
                {proposedChanges.map((c) => (
                  <li key={c.field}>
                    <span className="font-semibold">{c.label}</span> :
                    <span className="text-[#9CA3AF] line-through mx-1">{c.before}</span>→
                    <span className="text-[#0F766E] font-semibold ml-1">{c.after}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => onOpenChange(false)}
              className="h-10 px-4 rounded-lg border border-[#EFF0F6] text-[13px] font-semibold text-[#6B6C85] hover:bg-[#FAFAFE]"
            >
              Annuler
            </button>
            <button
              data-testid="amendment-submit"
              onClick={onSubmit}
              disabled={submitting || proposedChanges.length === 0 || !reason.trim()}
              className="h-10 px-5 rounded-lg brand-gradient text-white text-[13px] font-semibold flex items-center gap-1.5 shadow-sm hover:opacity-95 disabled:opacity-50"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FilePlus2 className="w-4 h-4" />}
              Créer & envoyer pour signature
            </button>
          </div>

          {/* Existing amendments */}
          <div className="pt-4 border-t border-[#EFF0F6]">
            <div className="text-[12.5px] font-semibold text-[#1A1B2E] mb-2">Historique des avenants</div>
            {loadingList ? (
              <div className="text-center py-3"><Loader2 className="w-4 h-4 animate-spin inline text-[#4F46E5]" /></div>
            ) : list.length === 0 ? (
              <div className="text-[12px] text-[#8E8FA5] italic">Aucun avenant pour cette session.</div>
            ) : (
              <div className="space-y-2">
                {list.map((a) => (
                  <div key={a.id} data-testid={`amendment-row-${a.id}`} className="rounded-xl border border-[#EFF0F6] p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <code className="text-[11px] bg-[#EEF2FF] text-[#4F46E5] px-1.5 py-0.5 rounded font-semibold">{a.id}</code>
                      <StatusBadge status={a.status} />
                      <span className="text-[11px] text-[#8E8FA5]">{a.created_at ? new Date(a.created_at).toLocaleString('fr-FR') : '—'}</span>
                      <div className="ml-auto flex items-center gap-2">
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/amendments/${a.id}/pdf`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[11.5px] text-[#4F46E5] font-semibold hover:underline"
                        >
                          PDF
                        </a>
                        {a.status === 'pending_signature' && (
                          <button
                            onClick={() => onCancelAmendment(a.id)}
                            className="text-[11.5px] text-[#EF4444] font-semibold hover:underline flex items-center gap-1"
                          >
                            <X className="w-3 h-3" /> Annuler
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="text-[12.5px] text-[#1A1B2E]">{a.reason}</div>
                    <div className="text-[11px] text-[#8E8FA5] mt-1">
                      {(a.changes || []).length} champ{(a.changes || []).length > 1 ? 's' : ''} modifié{(a.changes || []).length > 1 ? 's' : ''}
                      {a.client_email && <> · destinataire : {a.client_email}</>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending_signature: { bg: '#FEF3E7', fg: '#B45309', label: 'En attente signature' },
    applied: { bg: '#E6FBF7', fg: '#0F766E', label: 'Signé & appliqué', icon: CheckCircle2 },
    cancelled: { bg: '#F3F4F8', fg: '#6B6C85', label: 'Annulé' },
  };
  const m = map[status] || { bg: '#F3F4F8', fg: '#6B6C85', label: status };
  const Icon = m.icon;
  return (
    <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded" style={{ background: m.bg, color: m.fg }}>
      {Icon && <Icon className="w-3 h-3" />} {m.label}
    </span>
  );
}
